import { environment } from 'src/environments/environment';
import { IntercomComponent } from 'src/app/components/features/intercom/intercom.component';
import { RaygunComponent } from 'src/app/components/features/raygun/raygun.component';
import { Router, NavigationEnd } from '@angular/router';
import { AppConfig } from 'src/app/app.config';
import { Features } from 'src/app/models/features';
import { GoogleTagManagerComponent } from 'src/app/components/features/google-tag-manager/google-tag-manager.component';
import { DynamicComponentHostDirective } from 'src/app/components/shared/dynamic-component-loader/dynamic-component-host.directive';
import { Component, OnInit, ViewChild, ComponentRef, ComponentFactoryResolver, AfterViewInit, ViewContainerRef } from '@angular/core';

interface Scripts {
  name: string;
  src: string;
}

@Component({
  selector: 'app-external-scripts',
  templateUrl: './external-scripts.component.html',
  styleUrls: ['./external-scripts.component.scss']
})
export class ExternalScriptsComponent implements OnInit, AfterViewInit {

  @ViewChild(DynamicComponentHostDirective, { static: true }) dynamicComponentHost: DynamicComponentHostDirective;
  public gtmComponentRef: ComponentRef<GoogleTagManagerComponent>;
  public intercomComponentRef: ComponentRef<IntercomComponent>;
  public raygunComponentRef: ComponentRef<RaygunComponent>;

  private scripts: any = {};
  // TODO: refactor so that external scripts are actually loaded from config
  private ScriptStore: Scripts[] = [
    { name: 'googlemanager', src: `https://www.googletagmanager.com/gtag/js?id=${this.appConfig.getEnvironment().googleAnalyticsID}` }
  ];
  private googleTagManagerId: string;
  private intercomAppId: string;

  constructor(
    private appConfig: AppConfig,
    private router: Router,
    private componentFactoryResolver: ComponentFactoryResolver,
  ) {
    if ((<any>window).gtag && !!this.appConfig.getEnvironment().googleAnalyticsID) {
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          (<any>window).gtag('config', this.appConfig.getEnvironment().googleAnalyticsID, { 'page_path': event.urlAfterRedirects });
        }
      });
    }

    this.ScriptStore.forEach((script: Scripts) => {
      this.scripts[script.name] = {
        loaded: false,
        src: script.src
      };
    });

    this.load('googlemanager').then(data => {
      // Script Loaded Successfully
    }).catch(error => console.log(error));


    // TODO: combine config with external scripts that need to be loaded
    // If the environment requires other scrips to be loaded from the build system, prepend them into the store.
    // const preprendedScripts: Array<string> = this.appConfig.getEnvironmentConfig().clientsCustomScripts;
    // if (preprendedScripts && preprendedScripts.length > 0) {
    //   let i = 0;
    //   const ensureAllResolve = [];
    //   preprendedScripts.forEach((script: string) => {
    //     this.scripts[i] = {
    //       loaded: false,
    //       src: script,
    //     };
    //     ensureAllResolve.push(this.load(`${i}`));
    //     i++;
    //   });
    //   ensureAllResolve.reduce(async (prev) => {
    //     await prev;
    //   });
    // }
  }

  load(...scripts: string[]) {
    const promises: any[] = [];
    scripts.forEach((script) => promises.push(this.loadScript(script)));
    return Promise.all(promises);
  }

  loadScript(name: string) {
    return new Promise((resolve, reject) => {
      if (!this.scripts[name].loaded) {
        // load script
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = this.scripts[name].src;
        script.onload = () => {
          this.scripts[name].loaded = true;
          resolve({ script: name, loaded: true, status: 'Loaded' });
        };
        script.onerror = (error: any) => resolve({ script: name, loaded: false, status: 'Loaded' });
        document.getElementsByTagName('head')[0].appendChild(script);
      } else {
        resolve({ script: name, loaded: true, status: 'Already Loaded' });
      }
    });
  }
  
  ngOnInit() {
  }

  ngAfterViewInit() {
    if (this.appConfig.isFeatureEnabled(Features.GOOGLE_TAG_MANAGER)) {
      this.googleTagManagerId = (this.appConfig.getEnvironment().googleTagManagerID);
      if (this.googleTagManagerId) {
        this.loadGTMScript();
        this.loadGTMComponent();
      }
    }

    if (this.appConfig.isFeatureEnabled(Features.INTERCOM)) {
      this.intercomAppId = (this.appConfig.getFeatureProperty(Features.INTERCOM, 'intercomAppId'));
      if (this.intercomAppId) {
        this.loadIntercomScript();
        this.loadIntercomComponent();
      }
    }

    // Check if Raygun is enabled in the config and load the component if it is.
    if (this.appConfig.isFeatureEnabled(Features.RAYGUN)) {
      this.loadRaygunComponent();
    }
  }

  private loadGTMScript() {
    (function (w, d, s, l, i) {
      w[l] = w[l] || []; w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const f = d.getElementsByTagName(s)[0],
        j = d.createElement(s) as any,
        dl = l !== 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = '//www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', this.googleTagManagerId);
  }

  loadGTMComponent() {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(GoogleTagManagerComponent);
    const viewContainerRef: ViewContainerRef = (this.dynamicComponentHost) ? this.dynamicComponentHost.viewContainerRef : null;
    if (this.dynamicComponentHost) {
      this.gtmComponentRef = viewContainerRef.createComponent(componentFactory);
    }
  }

  private loadIntercomScript() {
    const w = window as any;
    const d = document as any;
    const ic = w.Intercom;
    if (typeof ic === "function") {
      ic('reattach_activator');
      ic('update', w.intercomSettings);
    } else {
      const i = function () { i['c'](arguments); };
      i['q'] = [];
      i['c'] = function (args) { i['q'].push(args); };
      w.Intercom = i;
      const s = d.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = 'https://widget.intercom.io/widget/' + this.intercomAppId;
      const x = d.getElementsByTagName('script')[0];
      x.parentNode.insertBefore(s, x);
    }
  }

  loadIntercomComponent() {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(IntercomComponent);
    const viewContainerRef: ViewContainerRef = (this.dynamicComponentHost) ? this.dynamicComponentHost.viewContainerRef : null;
    if (this.dynamicComponentHost) {
      this.intercomComponentRef = viewContainerRef.createComponent(componentFactory);
    }
  }

  private loadRaygunComponent() {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(RaygunComponent);
    const viewContainerRef: ViewContainerRef = (this.dynamicComponentHost) ? this.dynamicComponentHost.viewContainerRef : null;
    if (this.dynamicComponentHost) {
      this.raygunComponentRef = viewContainerRef.createComponent(componentFactory);
    }
  }
}
